import { Component, EventEmitter, Input, OnInit, Output, QueryList, ViewChildren } from '@angular/core';
import { FormGroup, FormBuilder, FormArray, FormControl, Validators } from '@angular/forms';
import { MweConstants } from 'src/app/constants/mwe-constants.service';
import { ListColumn } from 'src/app/models/list-column.model';
import { EndCustomerService } from 'src/app/services/end-customer.service';
import { AuthService } from 'src/app/services/auth.service';
import { Observable, of } from 'rxjs';
import { startWith, map, catchError } from 'rxjs/operators';
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { SweetAlertService } from 'src/app/services/sweetalert.service';

@Component({
  selector: 'app-member-profile',
  templateUrl: './member-profile.component.html',
  styleUrls: ['./member-profile.component.scss']
})
export class MemberProfileComponent implements OnInit {
  @ViewChildren(MatAutocompleteTrigger) autoCompletes!: QueryList<MatAutocompleteTrigger>;
  membersColumns: ListColumn[] = [
    { name: 'Photo/Name', property: 'name', visible: true, isModelProperty: true, isUserImage: true, imageKey: 'image' },
    { name: 'Position', property: 'position', visible: true, isModelProperty: true, hasChildProperty: true, childPropertyKey: 'name' },
    { name: 'Phone', property: 'phone', visible: true, isModelProperty: true, isText: true },
  ] as ListColumn[];

  @Input() membersForm!: FormGroup;
  @Input() actionType: any;

  positionDropdown: any[] = [];
  filteredPositions: Observable<any[]>[] = [];
  positionSearchControl = new FormControl('');

  accountType: any[] = [];
  filteredAccountType!: Observable<any[]>;
  filteredAccountTypes: Observable<any[]>[] = [];
  accountTypeSearchControls: FormControl[] = [];

  showEditForm: boolean = false;
  copyMemberForm: any;
  data: any = [];

  usertype = 'Individual';

  @Output() onUpdate: EventEmitter<any> = new EventEmitter();

  ngOnInit(): void {
    this.data = JSON.parse(JSON.stringify(this.membersForm.get('members')?.value));
    this.getPositionsDropdown();
    this.getAccountType();
    this.getFilteredAccountType();
    this.initializeAccountTypeSearchControls();
    this.updateFilteredAccountTypes();

    const nameValidators = [
      Validators.required,
      Validators.pattern(/^[A-Za-z .]+$/),
      Validators.maxLength(50)
    ];
    const emailValidators = [
      Validators.required,
      Validators.pattern("^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$"),
      Validators.maxLength(50)
    ];
    const phoneValidators = [
      Validators.required,
      Validators.pattern('^[0-9]{10,15}$'),
      Validators.maxLength(15),
      Validators.minLength(10)
    ];

    if (this.members.controls && this.members.controls.length) {
      this.members.controls.forEach((control) => {
        const membersFormControl = control as FormGroup;
        membersFormControl.get('name')?.setValidators(nameValidators);
        membersFormControl.get('email')?.setValidators(emailValidators);
        membersFormControl.get('phone')?.setValidators(phoneValidators);
        membersFormControl.get('name')?.updateValueAndValidity();
        membersFormControl.get('email')?.updateValueAndValidity();
        membersFormControl.get('phone')?.updateValueAndValidity();
      });
    }
  }

  getFilteredAccountType() {
    this.filteredAccountType = this.accountTypeSearchControls[0]?.valueChanges.pipe(
      startWith(''),
      map(value => this.filterAccountType(value || '')),
      catchError(() => of([]))
    ) || of([]);
  }

  initializeAccountTypeSearchControls() {
    this.accountTypeSearchControls = this.members.controls.map(() => new FormControl(''));
  }

  setupFilteredPositions() {
    this.filteredPositions = this.members.controls.map((control) => {
      const positionControl = control.get('position') as FormControl;
      return positionControl.valueChanges.pipe(
        startWith(''),
        map(value => {
          const filterValue = typeof value === 'string' ? value : value?.name || '';
          return this.filterPositions(filterValue);
        }),
        catchError(() => of([]))
      );
    });
  }

  updateFilteredPositions() {
    this.filteredPositions = this.members.controls.map((control) => {
      const positionControl = control.get('position') as FormControl;
      return positionControl.valueChanges.pipe(
        startWith(positionControl.value || ''),
        map(value => {
          const filterValue = typeof value === 'string' ? value : value?.name || '';
          return this.filterPositions(filterValue);
        }),
        catchError(() => of([]))
      );
    });
  }

  constructor(private fb: FormBuilder, private endCustomerService: EndCustomerService, private authService: AuthService, private sweetalertService: SweetAlertService) {
    window.addEventListener('scroll', this.scrollEvent, true);
  }

  scrollEvent = (event: any): void => {
    const target = event.target as HTMLElement;
    const isScrollingInsidePanel = this.autoCompletes.some(autoComplete => {
      if (autoComplete.panelOpen) {
        const panel = autoComplete.autocomplete.panel;
        return panel && panel.nativeElement.contains(target);
      }
      return false;
    });
    if (!isScrollingInsidePanel) {
      this.closeAllPanels();
    }
  };

  closeAllPanels() {
    this.autoCompletes.forEach(autoComplete => {
      if (autoComplete.panelOpen) {
        autoComplete.closePanel();
      }
    });
  }

  get members() {
    return this.membersForm.controls["members"] as FormArray;
  }

  getPositionsDropdown() {
    this.endCustomerService.executeGetRequestURL(MweConstants.positionUrl, null).subscribe(data => {
      if (data && data?.data && data.data.length > 0) {
        this.positionDropdown = data.data;
        this.members.controls.forEach((member, index) => {
          const currentValue = member.get('position')?.value;
          if (currentValue && typeof currentValue === 'object' && currentValue.id) {
            const position = this.positionDropdown.find((obj: any) => obj.id === currentValue.id);
            if (position) {
              member.get('position')?.patchValue(position);
            }
          }
        });
        this.updateFilteredPositions();
      }
    }, error => {
      console.error('Error fetching positions:', error);
    });
  }

  filterPositions(searchText: string): any[] {
    const filterValue = searchText.toLowerCase();
    return this.positionDropdown.filter((position: any) =>
      position?.name?.toLowerCase().includes(filterValue)
    );
  }

  onPositionSelected(event: any, index: number) {
    const selectedPosition = event.option.value;
    this.members.at(index).get('position')?.setValue(selectedPosition);
    this.updateFilteredPositions();
  }

  displayPosition(position: any): string {
    return position ? position.name : '';
  }

  getAccountType() {
    this.authService.getAccounttypes(this.usertype).subscribe(
      data => {
        if (data && data?.data && data.data.length > 0) {
          this.accountType = data.data;
          this.members.controls.forEach((member, index) => {
            const currentAccountId = member.get('accountId')?.value;
            if (currentAccountId) {
              const accountType = this.accountType.find((obj: any) => obj.id === currentAccountId);
              if (accountType) {
                this.accountTypeSearchControls[index].setValue(accountType.id);
              }
            }
          });
          this.updateFilteredAccountTypes();
        } else {
          console.warn('No account types data received');
        }
      },
      error => {
        console.error('Error fetching account types:', error);
      }
    );
  }

  updateFilteredAccountTypes() {
    this.filteredAccountTypes = this.accountTypeSearchControls.map((control, index) => {
      return control.valueChanges.pipe(
        startWith(control.value || ''),
        map(value => {
          const filterValue = typeof value === 'string' ? value : value?.name || '';
          return this.filterAccountType(filterValue);
        }),
        catchError(() => of([]))
      );
    });
  }

  filterAccountType(value: string): any[] {
    const filterValue = value.toLowerCase();
    return this.accountType.filter((account: any) =>
      account?.name?.toLowerCase().includes(filterValue)
    );
  }

  onAccountTypeSelected(event: MatAutocompleteSelectedEvent, index: number) {
    const selectedAccountType = event.option.value;
    if (selectedAccountType) {
      const memberGroup = this.members.at(index) as FormGroup;
      memberGroup.get('accountId')?.setValue(selectedAccountType.id);
      this.accountTypeSearchControls[index].setValue(selectedAccountType.id);
    }
  }

  displayAccountType = (accountTypeId: any): string => {
    if (!accountTypeId) return '';
    const account = this.accountType.find(acc => acc.id === accountTypeId);
    return account ? account.name : '';
  };

  addMember() {
    this.copyMemberForm = JSON.parse(JSON.stringify(this.membersForm.value));
    const newMember = this.fb.group({
      name: new FormControl('', [
        Validators.required,
        Validators.pattern(/^[A-Za-z\s]{1,50}$/)
      ]),
      position: new FormControl(null),
      phone: new FormControl('', [
        Validators.required,
        Validators.pattern('^[0-9]{10,15}$'),
        Validators.maxLength(15),
        Validators.minLength(10)
      ]),
      image: new FormControl(null),
      fileName: new FormControl(null),
      email: new FormControl('', [
        Validators.required,
        Validators.pattern('^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\\.[a-zA-Z0-9-.]+$')
      ]),
      accountId: new FormControl(null)
    });
    this.members.push(newMember);
    this.accountTypeSearchControls.push(new FormControl(''));
    this.showEditForm = true;
    this.updateFilteredPositions();
    this.updateFilteredAccountTypes();
  }

  getPositionControl(index: number): FormControl {
    const control = this.members.at(index).get('position');
    if (!control) {
      throw new Error(`Position control not found for member at index ${index}`);
    }
    return control as FormControl;
  }

  editMember() {
    this.copyMemberForm = JSON.parse(JSON.stringify(this.membersForm.value));
    this.showEditForm = true;
    this.updateMemberValidations();
  }

  validMembersEmail() {
    let pattern = false;
    for (let i = 0; i < this.members.controls.length; i++) {
      if (this.members.at(i).get('email')?.value && !this.members.at(i).get('email')?.value.match(/^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\\.[a-zA-Z0-9-.]+$/)) {
        this.members.at(i).get('email')?.setErrors({ pattern: true });
        pattern = true;
      } else {
        this.members.at(i).get('email')?.clearValidators();
        pattern = false;
      }
    }
    return pattern;
  }

  update() {
    this.members.markAllAsTouched();
    if (this.members.invalid) {
      return;
    }
    this.onUpdate.emit(20);
  }

  refresh() {
    this.showEditForm = false;
    this.updatePositionValue();
    this.data = JSON.parse(JSON.stringify(this.membersForm.get('members')?.value));
    this.updateMemberValidations();
  }

  updatePositionValue() {
    if (this.members.controls && this.members.controls.length > 0) {
      for (let i = 0; i < this.members.controls.length; i++) {
        if (this.members.at(i).get('position')?.value) {
          const position = this.positionDropdown.find((obj: any) => obj.id === this.members.at(i).get('position')?.value?.id);
          if (position) {
            this.members.at(i).get('position')?.patchValue(position);
          }
        }
      }
    }
  }

  cancel() {
    (this.membersForm.get('members') as FormArray).clear();
    if (this.copyMemberForm?.members && this.copyMemberForm.members.length > 0) {
      for (let i = 0; i < this.copyMemberForm.members.length; i++) {
        (this.membersForm.get('members') as FormArray).push(this.fb.group({
          id: new FormControl(null),
          active: new FormControl(null),
          createdBy: new FormControl(null),
          createdOn: new FormControl(null),
          modifiedBy: new FormControl(null),
          modifiedOn: new FormControl(null),
          name: new FormControl(null),
          position: new FormControl(null),
          phone: new FormControl('', [
            Validators.pattern('^[0-9]{10,15}$'),
            Validators.maxLength(15),
            Validators.minLength(10)
          ]),
          image: new FormControl(null),
          fileName: new FormControl(null),
          accountId: new FormControl(null)
        }));
      }
      (this.membersForm.get('members') as FormArray).patchValue(this.copyMemberForm.members);
    }
    this.updatePositionValue();
    this.showEditForm = false;
    this.data = JSON.parse(JSON.stringify(this.membersForm.get('members')?.value));
    this.updateMemberValidations();
  }

  deleteMember(i: number) {
    this.members.removeAt(i);
    this.accountTypeSearchControls.splice(i, 1);
    this.updateFilteredPositions();
    this.updateFilteredAccountTypes();
  }

  uploadImage(event: any, i: number) {
    event.preventDefault();
    let uploadData = new FormData();
    const file = event?.target?.files[0];
    const fileExtension = file?.name.split('.').pop()?.toLowerCase();
    if (fileExtension !== 'png' && fileExtension !== 'jpg' && fileExtension !== 'jpeg') {
      this.sweetalertService.showError('Error', 'Only png, jpg, jpeg files are allowed');
      return;
    }
    uploadData.append('file', file);
    let randomNumber = Math.floor(Math.random() * 10) + 1;
    uploadData.append('destinationKey', randomNumber + '');
    this.endCustomerService.executePostRequestForAuth(MweConstants.uploadFile, uploadData).subscribe(data => {
      if (data && data?.data) {
        this.members.at(i).get('image')?.patchValue(data.data);
        this.members.at(i).get('fileName')?.patchValue(file?.name);
      }
    }, error => {});
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
  }

  clearImage(i: number) {
    this.members.at(i).get('image')?.reset();
    this.members.at(i).get('fileName')?.reset();
  }

  updateMemberValidations() {
    for (let i = 0; i < this.members.controls.length; i++) {
      this.members.at(i).get('name')?.setValidators([
        Validators.required,
        Validators.pattern(/^[A-Za-z\s]{1,50}$/)
      ]);
      this.members.at(i).get('phone')?.setValidators([
        Validators.required,
        Validators.pattern('^[0-9]{10,15}$'),
        Validators.maxLength(15),
        Validators.minLength(10)
      ]);
      this.members.at(i).get('email')?.setValidators([
        Validators.required,
        Validators.pattern('^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\\.[a-zA-Z0-9-.]+$')
      ]);
      this.members.at(i).get('name')?.updateValueAndValidity();
      this.members.at(i).get('phone')?.updateValueAndValidity();
      this.members.at(i).get('email')?.updateValueAndValidity();
    }
  }

  handleKeyDown(event: KeyboardEvent) {
    if (event.key === "Enter") {
      event.preventDefault();
    }
  }
}